<template>
  <div class="donut-chart">
    <div class="donut-chart__row">
      <div class="donut-chart__ring-wrap">
        <svg
          class="donut-chart__ring"
          viewBox="0 0 100 100"
        >
          <circle
            class="donut-chart__track"
            cx="50"
            cy="50"
            r="40"
          />
          <circle
            v-for="(segment, index) in segments"
            :key="index"
            class="donut-chart__segment"
            :class="{ 'donut-chart__segment--dim': activeIndex !== null && activeIndex !== index }"
            :data-field="dataFieldFor(index)"
            cx="50"
            cy="50"
            r="40"
            :stroke="segment.color"
            :stroke-dasharray="segment.dashArray"
            :stroke-dashoffset="segment.dashOffset"
            @mouseenter="hoveredIndex = index"
            @mouseleave="hoveredIndex = null"
            @click.stop="toggleHover(index)"
          />
        </svg>
        <div class="donut-chart__center">
          <div class="donut-chart__center-value">
            {{ total }}
          </div>
          <div
            v-if="totalLabel"
            class="donut-chart__center-label"
          >
            {{ totalLabel }}
          </div>
        </div>
        <div
          v-if="activeIndex !== null"
          class="donut-chart__tooltip"
          :style="tooltipStyle"
        >
          {{ items[activeIndex].name }} · {{ items[activeIndex].value }} · {{ items[activeIndex].pct }}
        </div>
      </div>
      <div class="donut-chart__legend">
        <div
          v-for="(item, index) in items"
          :key="index"
          class="donut-chart__legend-item"
          :class="{ 'donut-chart__legend-item--dim': activeIndex !== null && activeIndex !== index }"
          :data-field="dataFieldFor(index)"
          @mouseenter="hoveredIndex = index"
          @mouseleave="hoveredIndex = null"
        >
          <span
            class="donut-chart__dot"
            :style="{ background: item.color }"
          />
          <span class="donut-chart__legend-name">{{ item.name }}</span>
          <span class="donut-chart__legend-value">{{ item.value }}</span>
          <span class="donut-chart__legend-pct">{{ item.pct }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DataFieldByIndex from './DataFieldByIndex'

const CIRCUMFERENCE = 2 * Math.PI * 40

export default {
  name: 'BaseDonutChart',
  mixins: [DataFieldByIndex],
  props: {
    items: {
      type: Array,
      required: true
    },
    total: {
      type: [String, Number],
      required: true
    },
    totalLabel: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      hoveredIndex: null,
      pinnedIndex: null
    }
  },
  computed: {
    activeIndex() {
      return this.pinnedIndex !== null ? this.pinnedIndex : this.hoveredIndex
    },
    segments() {
      let cumulativePct = 0
      return this.items.map((item) => {
        const pct = item.pctValue || 0
        const dashArray = `${(pct / 100) * CIRCUMFERENCE} ${CIRCUMFERENCE}`
        const dashOffset = -((cumulativePct / 100) * CIRCUMFERENCE)
        cumulativePct += pct
        return { color: item.color, dashArray, dashOffset }
      })
    },
    tooltipStyle() {
      if (this.activeIndex === null) {
        return {}
      }
      let cumulativePct = 0
      for (let i = 0; i < this.activeIndex; i += 1) {
        cumulativePct += this.items[i].pctValue || 0
      }
      const segmentPct = this.items[this.activeIndex].pctValue || 0
      const midPct = cumulativePct + segmentPct / 2
      const angle = (midPct / 100) * 360 - 90
      const radians = (angle * Math.PI) / 180
      const x = 50 + 46 * Math.cos(radians)
      const y = 50 + 46 * Math.sin(radians)
      return {
        left: `${x}%`,
        top: `${y}%`
      }
    }
  },
  methods: {
    toggleHover(index) {
      this.pinnedIndex = this.pinnedIndex === index ? null : index
    }
  }
}
</script>

<style lang="scss">
.donut-chart {
  &__row {
    display: flex;
    align-items: center;
    gap: 28px;
    flex-wrap: wrap;
  }

  &__ring-wrap {
    position: relative;
    width: 144px;
    height: 144px;
    flex: none;
  }

  &__ring {
    width: 100%;
    height: 100%;
    transform: rotate(-90deg);
  }

  &__track {
    fill: none;
    stroke: var(--ds-color-black-010);
    stroke-width: 16;
  }

  &__segment {
    fill: none;
    stroke-width: 16;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &--dim {
      opacity: 0.35;
    }
  }

  &__center {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    text-align: center;
  }

  &__center-value {
    font-size: 19px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--v-textPrimary-base);
  }

  &__center-label {
    font-size: 10px;
    color: var(--ds-color-black-055);
    max-width: 80px;
  }

  &__tooltip {
    position: absolute;
    transform: translate(-50%, -50%);
    z-index: 10;
    background: rgba(97, 97, 97, 0.95);
    color: #fff;
    font-size: 11px;
    font-weight: 500;
    padding: 5px 9px;
    border-radius: 5px;
    white-space: nowrap;
    pointer-events: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  }

  &__legend {
    display: flex;
    flex-direction: column;
    gap: 10px;
    font-size: 12.5px;
  }

  &__legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &--dim {
      opacity: 0.45;
    }
  }

  &__dot {
    width: 9px;
    height: 9px;
    border-radius: 2px;
    flex: none;
  }

  &__legend-name {
    color: var(--ds-color-black-055);
    min-width: 96px;
  }

  &__legend-value {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    margin-left: auto;
    color: var(--v-textPrimary-base);
  }

  &__legend-pct {
    color: var(--ds-color-black-040);
    font-size: 11px;
    width: 44px;
    text-align: right;
  }
}
</style>
